import { Component } from '@theme/component';
import { cartAdd } from '@theme/na-cart-api';
import { formatShopMoney } from '@theme/money-formatting';
import { closeRecommendedProductPopup, renderRecommendedProductPopup } from '@theme/na-cart-free-gifts';

class RecommendedProductAddComponent extends Component {
    /**
     * @param {Event} event
     */
    async add(event) {
        event.preventDefault();

        const variantId = this.getAttribute('data-current-variant');
        if (!variantId) return;

        const wrapper = this.closest('.recommended-product-wrapper');
        this.classList.add('loading');
        wrapper?.classList.add('is-loading');

        const recommendedItem = this.closest('.recommended-item');
        const mainProductId = /** @type {HTMLElement | null} */ (recommendedItem)?.dataset.mainProductId;
        /** @type {{ id: string, quantity: number, properties: Record<string, string> }} */
        const itemToAdd = {
            id: variantId,
            quantity: 1,
            properties: { _recommended_product: 'true' },
        };
        if (mainProductId) itemToAdd.properties._main_product_id = mainProductId;
        const isPopup = Boolean(this.closest('.popup-mode'));

        try {
            await cartAdd([itemToAdd]);
            if (isPopup && renderRecommendedProductPopup()) return;
            closeRecommendedProductPopup();
            const drawer = document.querySelector('cart-drawer-component');
            if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function') {
                /** @type {any} */ (drawer).open();
            }
        } catch (error) {
            console.error('[na-recommended-product-add] /cart/add.js failed', error);
        } finally {
            this.classList.remove('loading');
            wrapper?.classList.remove('is-loading');
        }
    }

    /**
     * @param {Event} event
     */
    onSwatchChange(event) {
        const input = /** @type {HTMLInputElement} */ (event.target);
        if (!input.checked) return;

        const recommendedItem = input.closest('.recommended-item');
        const variantId = input.dataset.variantId;
        if (!recommendedItem || !variantId) return;

        this.setAttribute('data-current-variant', variantId);
        const addToCartText = this.getAttribute('data-add-text') || 'Add to Cart';
        const buttonText = this.querySelector('.button-text');
        if (buttonText) buttonText.textContent = addToCartText;

        const image = recommendedItem.querySelector('.image img');
        if (image && input.dataset.variantImage) {
            const src = input.dataset.variantImage.startsWith('//')
                ? `https:${input.dataset.variantImage}`
                : input.dataset.variantImage;
            image.setAttribute('src', src);
            image.setAttribute('srcset', src);
        }

        updateRecommendedPrice(recommendedItem, this, input);
    }
}

class RecommendedProductItemComponent extends Component {
    /**
     * @param {Event} event
     */
    onSwatchChange(event) {
        const addButton = this.querySelector('recommended-product-add-component');
        if (addButton instanceof RecommendedProductAddComponent) {
            addButton.onSwatchChange(event);
        }
    }
}

/**
 * @param {Element} recommendedItem
 * @param {Element} addButton
 * @param {HTMLInputElement} input
 */
function updateRecommendedPrice(recommendedItem, addButton, input) {
    const priceWrapper = recommendedItem.querySelector('.price-wrapper');
    if (!priceWrapper) return;

    const variantPrice = parseInt(input.dataset.variantPrice ?? '0', 10);
    const variantComparePrice = parseInt(input.dataset.variantComparePrice ?? '0', 10);
    const variantDiscountMetafield = input.dataset.variantDiscountMetafield;
    const productDiscountMetafield = addButton.getAttribute('data-product-discount-metafield');

    let finalPrice = variantPrice;
    let finalComparePrice = variantComparePrice;
    let discount = 0;

    if (variantComparePrice && variantComparePrice > variantPrice) {
        discount = variantComparePrice - variantPrice;
        finalComparePrice = variantComparePrice;
    } else if (variantDiscountMetafield && variantDiscountMetafield !== 'null' && variantDiscountMetafield !== '') {
        discount = parseFloat(variantDiscountMetafield) * 100;
        finalComparePrice = variantPrice + discount;
    } else if (productDiscountMetafield && productDiscountMetafield !== 'null' && productDiscountMetafield !== '') {
        discount = parseFloat(productDiscountMetafield) * 100;
        finalComparePrice = variantPrice + discount;
    }

    priceWrapper.replaceChildren();
    if (discount > 0) {
        appendPriceSpan(priceWrapper, 'price-compare', formatShopMoney(finalComparePrice));
        appendPriceSpan(priceWrapper, 'price-current', formatShopMoney(finalPrice));
        appendPriceSpan(priceWrapper, 'discount-badge', `${formatShopMoney(discount, { noDecimals: true })} OFF`);
    } else {
        appendPriceSpan(priceWrapper, 'price-current', formatShopMoney(finalPrice));
    }
}

/**
 * @param {Element} parent
 * @param {string} className
 * @param {string} text
 */
function appendPriceSpan(parent, className, text) {
    const span = document.createElement('span');
    span.className = className;
    span.textContent = text;
    parent.appendChild(span);
}

if (!customElements.get('recommended-product-add-component')) {
    customElements.define('recommended-product-add-component', RecommendedProductAddComponent);
}

if (!customElements.get('recommended-product-item-component')) {
    customElements.define('recommended-product-item-component', RecommendedProductItemComponent);
}
